import { type UserRole } from '~/data/schemas/user';

const NO_ACCESS_ROLE = 'none' as const;

// List ot features
export const PORTFOLIO_PERFORMANCE_KPIS = 'canAccessPortfolioPerformanceKPIs' as const;
export const TICKETS_PERFORMANCE = 'canAccessTicketsPerformance' as const;
export const BOTTOM_FIVE_PERFORMANCE = 'canAccessBottomFivePerformance' as const;
export const NAVIGATE_TO_BACKLOG_PAGE = 'canAccessBacklogPage' as const;
export const NAVIGATE_TO_PORTFOLIO_PAGE = 'canAccessPortfolioPage' as const;
export const NAVIGATE_TO_PROPERTY_PAGE = 'canAccessPropertyDetailsPage' as const;
export const NAVIGATE_TO_TICKETS_PAGE = 'canAccessTicketsPage' as const;
export const NAVIGATE_TO_SPENDING_PAGE = 'canAccessInvoicesPage' as const;
export const NAVIGATE_TO_CONTRACTS_PAGE = 'canAccessContractsPage' as const;
export const UNIVERSAL_SEARCH = 'canAccessUniversalSearch' as const;
export const UPLOAD_DOCUMENT_FORM = 'canAccessUploadDocumentForm' as const;
export const DOCUMENT_REVIEW = 'canAccessDocumentReview' as const;
export const ACTIONS_ON_TICKETS = 'canAccessActionMenuOnTickets' as const;
export const DOWNLOAD_TICKET_FILE = 'canAccessTicketFile' as const;
export const TICKET_DATA_STATUS = 'canAccessTicketsDataStatus' as const;
export const EDIT_CONTRACT = 'canAccessEditContract' as const;
export const EDIT_ELEVATOR = 'canAccessEditElevator' as const;
export const INSIGHTS_TAB = 'canAccessInsightsTab' as const;
export const ACTIONS_ON_SPENDING = 'canAccessActionsOnSpending' as const;
export const PROPERTY_LINKS_IN_SPENDING = 'canAccessPropertyLinksInSpending' as const;
export const ASK_OTTO = 'canAskOtto' as const;
// Knowledge entries are private to the consulting company that wrote them.
export const CAPTURE_KNOWLEDGE = 'canCaptureKnowledge' as const;
export const MARK_MAINTENANCE_NOT_PERFORMED = 'canMarkMaintenanceNotPerformed' as const;
export type CAN_ACCESS_TO =
  | typeof NAVIGATE_TO_BACKLOG_PAGE
  | typeof PORTFOLIO_PERFORMANCE_KPIS
  | typeof TICKETS_PERFORMANCE
  | typeof BOTTOM_FIVE_PERFORMANCE
  | typeof NAVIGATE_TO_PORTFOLIO_PAGE
  | typeof NAVIGATE_TO_PROPERTY_PAGE
  | typeof NAVIGATE_TO_TICKETS_PAGE
  | typeof NAVIGATE_TO_SPENDING_PAGE
  | typeof NAVIGATE_TO_CONTRACTS_PAGE
  | typeof UNIVERSAL_SEARCH
  | typeof UPLOAD_DOCUMENT_FORM
  | typeof DOCUMENT_REVIEW
  | typeof ACTIONS_ON_TICKETS
  | typeof DOWNLOAD_TICKET_FILE
  | typeof TICKET_DATA_STATUS
  | typeof EDIT_CONTRACT
  | typeof EDIT_ELEVATOR
  | typeof INSIGHTS_TAB
  | typeof ACTIONS_ON_SPENDING
  | typeof PROPERTY_LINKS_IN_SPENDING
  | typeof ASK_OTTO
  | typeof CAPTURE_KNOWLEDGE
  | typeof MARK_MAINTENANCE_NOT_PERFORMED;
// end list of features

const NO_ACCESS: Record<CAN_ACCESS_TO, boolean> = {
  [NAVIGATE_TO_BACKLOG_PAGE]: false,
  [PORTFOLIO_PERFORMANCE_KPIS]: false,
  [TICKETS_PERFORMANCE]: false,
  [BOTTOM_FIVE_PERFORMANCE]: false,
  [NAVIGATE_TO_PROPERTY_PAGE]: false,
  [NAVIGATE_TO_TICKETS_PAGE]: false,
  [UNIVERSAL_SEARCH]: false,
  [UPLOAD_DOCUMENT_FORM]: false,
  [DOCUMENT_REVIEW]: false,
  [ACTIONS_ON_TICKETS]: false,
  [DOWNLOAD_TICKET_FILE]: false,
  [TICKET_DATA_STATUS]: false,
  [NAVIGATE_TO_SPENDING_PAGE]: false,
  [NAVIGATE_TO_CONTRACTS_PAGE]: false,
  [NAVIGATE_TO_PORTFOLIO_PAGE]: false,
  [EDIT_CONTRACT]: false,
  [EDIT_ELEVATOR]: false,
  [INSIGHTS_TAB]: false,
  [ACTIONS_ON_SPENDING]: false,
  [PROPERTY_LINKS_IN_SPENDING]: false,
  [ASK_OTTO]: false,
  [CAPTURE_KNOWLEDGE]: false,
  [MARK_MAINTENANCE_NOT_PERFORMED]: false,
};

const featuresByRole: Record<
  NonNullable<UserRole> | typeof NO_ACCESS_ROLE,
  Record<CAN_ACCESS_TO, boolean>
> = {
  consultant: {
    ...NO_ACCESS,
    [NAVIGATE_TO_BACKLOG_PAGE]: true,
    [PORTFOLIO_PERFORMANCE_KPIS]: true,
    [TICKETS_PERFORMANCE]: true,
    [BOTTOM_FIVE_PERFORMANCE]: true,
    [NAVIGATE_TO_PORTFOLIO_PAGE]: true,
    [NAVIGATE_TO_PROPERTY_PAGE]: true,
    [NAVIGATE_TO_TICKETS_PAGE]: true,
    [NAVIGATE_TO_SPENDING_PAGE]: true,
    [NAVIGATE_TO_CONTRACTS_PAGE]: true,
    [UNIVERSAL_SEARCH]: true,
    [UPLOAD_DOCUMENT_FORM]: true,
    [DOCUMENT_REVIEW]: true,
    [ACTIONS_ON_TICKETS]: true,
    [DOWNLOAD_TICKET_FILE]: true,
    [TICKET_DATA_STATUS]: true,
    [EDIT_CONTRACT]: true,
    [EDIT_ELEVATOR]: true,
    [INSIGHTS_TAB]: true,
    [ACTIONS_ON_SPENDING]: true,
    [PROPERTY_LINKS_IN_SPENDING]: true,
    [ASK_OTTO]: true,
    [CAPTURE_KNOWLEDGE]: true,
    [MARK_MAINTENANCE_NOT_PERFORMED]: true,
  },
  client: {
    ...NO_ACCESS,
    [PORTFOLIO_PERFORMANCE_KPIS]: true,
    [NAVIGATE_TO_PORTFOLIO_PAGE]: true,
    [NAVIGATE_TO_TICKETS_PAGE]: true,
    [DOWNLOAD_TICKET_FILE]: true,
    [NAVIGATE_TO_SPENDING_PAGE]: true,
    [NAVIGATE_TO_CONTRACTS_PAGE]: true,
    [INSIGHTS_TAB]: true,
  },
  service_provider: {
    ...NO_ACCESS,
  },
  [NO_ACCESS_ROLE]: {
    ...NO_ACCESS,
    [INSIGHTS_TAB]: true,
  },
};

const getFeaturesByRole = (userRole: UserRole): Record<CAN_ACCESS_TO, boolean> => {
  // `userRole` is trusted to be one of the known roles at the type level, but
  // the server can ship a new role before the client regenerates its types —
  // fall back to no access rather than let an unknown key crash the lookup.
  return featuresByRole[userRole] ?? NO_ACCESS;
};

/**
 * `canAccess` verifies if a user with an specific role has access to certain feature
 * @param userRole
 * @param feature
 * @returns
 */
export const canAccess = (userRole: UserRole, feature: CAN_ACCESS_TO) => {
  const features = getFeaturesByRole(userRole);
  return features[feature] ?? false;
};

export const getDefaultRouteForRole = (userRole: UserRole) => {
  if (userRole === 'none') {
    return '/reports';
  }
  return '/portfolio';
};
